import React, { useState, useEffect, useCallback } from 'react';
import {
  fetchSymptoms,
  logSymptom,
  deleteSymptom,
  seedMockData,
} from '../services/symptomService';
import {
  filterByRange,
  getRecommendations,
  summarizeForChatbot,
  SEVERITY_COLORS,
  SEVERITY_BG,
  SEVERITY_LABELS,
} from '../utils/symptomHelpers';
import SymptomForm from '../components/SymptomForm';
import SymptomTrendChart from '../components/SymptomTrendChart';

// Recommendation card colour palette
const REC_STYLE = {
  urgent:  { bg: '#FEF2F2', border: '#FECACA', titleColor: '#991B1B' },
  warning: { bg: '#FFFBEB', border: '#FDE68A', titleColor: '#92400E' },
  info:    { bg: '#EFF6FF', border: '#BFDBFE', titleColor: '#1E40AF' },
  tip:     { bg: '#E3F0EE', border: '#BAD5CF', titleColor: '#338477' },
};

const TABS = [
  { key: 'daily',   label: 'Today'   },
  { key: 'weekly',  label: 'Weekly'  },
  { key: 'monthly', label: 'Monthly' },
];

export default function SymptomTracker({ user, onOpenChat }) {
  const [symptoms, setSymptoms]   = useState([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState('');
  const [showForm, setShowForm]   = useState(false);
  const [saving, setSaving]       = useState(false);
  const [range, setRange]         = useState('weekly');

  const username = user?.username || '';

  const loadSymptoms = useCallback(async () => {
    if (!username) return;
    setLoading(true);
    setError('');
    try {
      seedMockData(username);
      const data = await fetchSymptoms(username);
      setSymptoms(data);
    } catch {
      setError('Could not reach the server. Showing cached data.');
    } finally {
      setLoading(false);
    }
  }, [username]);

  useEffect(() => {
    loadSymptoms();
  }, [loadSymptoms]);

  const handleSave = async entry => {
    setSaving(true);
    try {
      const saved = await logSymptom(username, entry);
      setSymptoms(prev => [saved, ...prev]);
      setShowForm(false);
    } catch {
      setError('Failed to save symptom. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async id => {
    const idStr = String(id);
    await deleteSymptom(idStr);
    setSymptoms(prev => prev.filter(s => String(s._id) !== idStr));
  };

  const handleChatWithSymptoms = () => {
    const summary = summarizeForChatbot(filteredSymptoms);
    localStorage.setItem('symptomContext', summary);
    if (onOpenChat) onOpenChat(summary);
  };

  const filteredSymptoms = filterByRange(symptoms, range).sort(
    (a, b) => new Date(b.loggedAt) - new Date(a.loggedAt)
  );
  const recommendations = getRecommendations(symptoms);

  const rangeLabel =
    range === 'daily' ? 'today' : range === 'weekly' ? 'this week' : 'this month';

  return (
    <div style={{ maxWidth: '720px', margin: '0 auto', paddingBottom: '40px' }}>

      {/* ── Page header ── */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: '10px',
          marginBottom: '20px',
        }}
      >
        <div>
          <h2 style={{ fontSize: '22px', fontWeight: '700', color: 'var(--teal-dark)', margin: 0 }}>
            Symptom Tracker
          </h2>
          <p style={{ fontSize: '13px', color: '#718096', margin: '3px 0 0' }}>
            {symptoms.length} total entr{symptoms.length === 1 ? 'y' : 'ies'}
          </p>
        </div>
        <button
          type="button"
          onClick={() => setShowForm(true)}
          style={{
            padding: '10px 18px',
            background: 'var(--teal)',
            color: 'white',
            border: 'none',
            borderRadius: '10px',
            fontSize: '14px',
            fontWeight: '600',
            cursor: 'pointer',
            fontFamily: 'inherit',
          }}
        >
          + Log Symptom
        </button>
      </div>

      {/* ── Error banner ── */}
      {error && (
        <div
          style={{
            marginBottom: '16px',
            padding: '12px',
            background: '#FCEBEB',
            border: '1px solid #FECACA',
            borderRadius: '8px',
            color: '#991B1B',
            fontSize: '14px',
          }}
        >
          {error}
        </div>
      )}

      {/* ── Tab bar ── */}
      <div
        style={{
          display: 'flex',
          background: '#f1f5f9',
          borderRadius: '10px',
          padding: '3px',
          marginBottom: '20px',
          gap: '2px',
        }}
      >
        {TABS.map(t => (
          <button
            key={t.key}
            type="button"
            onClick={() => setRange(t.key)}
            style={{
              flex: 1,
              padding: '9px',
              borderRadius: '8px',
              border: 'none',
              background: range === t.key ? 'white' : 'transparent',
              color: range === t.key ? 'var(--teal)' : '#718096',
              fontSize: '14px',
              fontWeight: range === t.key ? '600' : '400',
              cursor: 'pointer',
              boxShadow: range === t.key ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
              transition: 'all 0.15s',
              fontFamily: 'inherit',
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* ── Trend Chart card ── */}
      <div
        style={{
          background: 'white',
          borderRadius: '12px',
          border: '1px solid #e2e8f0',
          padding: '20px',
          marginBottom: '16px',
        }}
      >
        <h3 style={{ fontSize: '15px', fontWeight: '600', color: '#1a1a1a', margin: '0 0 2px' }}>
          Symptom Trend
        </h3>
        <p style={{ fontSize: '12px', color: '#718096', margin: '0 0 16px' }}>
          Bar height = avg severity · colour = worst severity reported
        </p>

        {loading ? (
          <div
            style={{
              height: '120px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#718096',
              fontSize: '14px',
            }}
          >
            Loading…
          </div>
        ) : (
          <SymptomTrendChart symptoms={symptoms} range={range} />
        )}

        {/* Legend */}
        <div
          style={{
            display: 'flex',
            gap: '14px',
            marginTop: '12px',
            flexWrap: 'wrap',
          }}
        >
          {['mild', 'moderate', 'severe', 'very_severe'].map(sev => (
            <div
              key={sev}
              style={{ display: 'flex', alignItems: 'center', gap: '5px' }}
            >
              <div
                style={{
                  width: '10px',
                  height: '10px',
                  borderRadius: '2px',
                  background: SEVERITY_COLORS[sev],
                  flexShrink: 0,
                }}
              />
              <span style={{ fontSize: '11px', color: '#718096' }}>
                {SEVERITY_LABELS[sev]}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* ── Symptom list card ── */}
      <div
        style={{
          background: 'white',
          borderRadius: '12px',
          border: '1px solid #e2e8f0',
          overflow: 'hidden',
          marginBottom: '16px',
        }}
      >
        <div
          style={{
            padding: '14px 20px',
            borderBottom: '1px solid #e2e8f0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <h3 style={{ fontSize: '15px', fontWeight: '600', color: '#1a1a1a', margin: 0 }}>
            Logged Symptoms
          </h3>
          <span style={{ fontSize: '12px', color: '#718096' }}>
            {filteredSymptoms.length} {rangeLabel}
          </span>
        </div>

        {loading ? (
          <div
            style={{
              padding: '40px',
              textAlign: 'center',
              color: '#718096',
              fontSize: '14px',
            }}
          >
            Loading…
          </div>
        ) : filteredSymptoms.length === 0 ? (
          <div style={{ padding: '48px 20px', textAlign: 'center' }}>
            <div style={{ fontSize: '36px', marginBottom: '12px' }}>📋</div>
            <p
              style={{
                fontSize: '15px',
                fontWeight: '600',
                color: '#374151',
                margin: '0 0 6px',
              }}
            >
              No symptoms logged {rangeLabel}
            </p>
            <p style={{ fontSize: '13px', color: '#718096', margin: 0 }}>
              Tap "Log Symptom" to record your first entry.
            </p>
          </div>
        ) : (
          <div>
            {filteredSymptoms.map(s => (
              <div
                key={String(s._id)}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '12px',
                  padding: '13px 20px',
                  borderBottom: '1px solid #f8fafc',
                }}
              >
                {/* Severity dot */}
                <div
                  style={{
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    background: SEVERITY_COLORS[s.severity],
                    marginTop: '6px',
                    flexShrink: 0,
                  }}
                />

                {/* Content */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      flexWrap: 'wrap',
                    }}
                  >
                    <span
                      style={{
                        fontSize: '14px',
                        fontWeight: '600',
                        color: '#1a1a1a',
                      }}
                    >
                      {s.name}
                    </span>
                    <span
                      style={{
                        fontSize: '11px',
                        fontWeight: '600',
                        padding: '2px 8px',
                        borderRadius: '10px',
                        background: SEVERITY_BG[s.severity],
                        color: SEVERITY_COLORS[s.severity],
                      }}
                    >
                      {SEVERITY_LABELS[s.severity]}
                    </span>
                  </div>

                  {s.note ? (
                    <p
                      style={{
                        fontSize: '13px',
                        color: '#718096',
                        margin: '3px 0 0',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                      }}
                    >
                      {s.note}
                    </p>
                  ) : null}

                  <p
                    style={{
                      fontSize: '11px',
                      color: '#9ca3af',
                      margin: '4px 0 0',
                    }}
                  >
                    {new Date(s.loggedAt).toLocaleString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      hour: 'numeric',
                      minute: '2-digit',
                      hour12: true,
                    })}
                  </p>
                </div>

                {/* Delete */}
                <button
                  type="button"
                  onClick={() => handleDelete(String(s._id))}
                  aria-label="Delete symptom"
                  title="Delete"
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#d1d5db',
                    fontSize: '20px',
                    lineHeight: 1,
                    cursor: 'pointer',
                    padding: '2px 4px',
                    flexShrink: 0,
                  }}
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Recommendations card ── */}
      <div
        style={{
          background: 'white',
          borderRadius: '12px',
          border: '1px solid #e2e8f0',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            padding: '14px 20px',
            borderBottom: '1px solid #e2e8f0',
          }}
        >
          <h3
            style={{
              fontSize: '15px',
              fontWeight: '600',
              color: '#1a1a1a',
              margin: '0 0 2px',
            }}
          >
            Management Recommendations
          </h3>
          <p style={{ fontSize: '12px', color: '#718096', margin: 0 }}>
            Based on your symptom log from the last 7 days
          </p>
        </div>

        <div
          style={{
            padding: '12px',
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
          }}
        >
          {recommendations.map((rec, i) => {
            const style = REC_STYLE[rec.level] || REC_STYLE.tip;
            return (
              <div
                key={i}
                style={{
                  padding: '12px 14px',
                  borderRadius: '10px',
                  background: style.bg,
                  border: `1px solid ${style.border}`,
                  display: 'flex',
                  gap: '10px',
                  alignItems: 'flex-start',
                }}
              >
                <span style={{ fontSize: '18px', lineHeight: 1, flexShrink: 0 }}>
                  {rec.icon}
                </span>
                <div>
                  <p
                    style={{
                      fontSize: '13px',
                      fontWeight: '700',
                      color: style.titleColor,
                      margin: '0 0 2px',
                    }}
                  >
                    {rec.title}
                  </p>
                  <p
                    style={{
                      fontSize: '13px',
                      color: '#374151',
                      margin: 0,
                      lineHeight: '1.45',
                    }}
                  >
                    {rec.text}
                  </p>
                </div>
              </div>
            );
          })}
        </div>

        {/* Chatbot integration hook */}
        {filteredSymptoms.length > 0 && onOpenChat && (
          <div style={{ padding: '0 12px 12px' }}>
            <button
              type="button"
              onClick={handleChatWithSymptoms}
              style={{
                width: '100%',
                padding: '12px',
                background: 'white',
                color: 'var(--teal)',
                border: '1.5px solid var(--teal)',
                borderRadius: '10px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer',
                fontFamily: 'inherit',
              }}
            >
              💬 Discuss these symptoms with Reaxano AI
            </button>
          </div>
        )}
      </div>

      {/* ── Log Symptom modal ── */}
      {showForm && (
        <SymptomForm
          onSave={handleSave}
          onClose={() => setShowForm(false)}
          saving={saving}
        />
      )}
    </div>
  );
}
